/**
 * Shared utility functions for report processing across PatientPage, EditableDocumentPage, and SchedulesTable.
 * Supports PFT, Imaging (radiology/x-ray/CT/MRI/etc.), CBC, and Sleep Study report types.
 */

import { getSupabase } from "../components/supabaseClient";
import { bucketName_files } from "../components/env";
import { getCurrentUserName } from "./formProcessingUtils";

// Get bucket name with fallback
const getBucketName = () => {
  return bucketName_files && bucketName_files !== "undefined" && bucketName_files !== undefined
    ? bucketName_files
    : "files";
};

/**
 * Report type constants
 */
export const REPORT_TYPES = {
  PFT: "PFT",
  CXR: "CXR",
  CBC: "CBC",
  SLEEP_STUDY: "SLEEP_STUDY",
};

/**
 * Display names for report types
 */
export const REPORT_TYPE_LABELS = {
  [REPORT_TYPES.PFT]: "PFT (Pulmonary Function Test)",
  [REPORT_TYPES.CXR]: "Imaging (Radiology / X-Ray / CT / MRI)",
  [REPORT_TYPES.CBC]: "CBC / Lab Results",
  [REPORT_TYPES.SLEEP_STUDY]: "Sleep Study",
};

/**
 * Short labels for report types (used in button labels, etc.)
 */
export const REPORT_TYPE_SHORT_LABELS = {
  [REPORT_TYPES.PFT]: "PFT",
  [REPORT_TYPES.CXR]: "Imaging",
  [REPORT_TYPES.CBC]: "CBC/Labs",
  [REPORT_TYPES.SLEEP_STUDY]: "Sleep Study",
};

/**
 * Classify a report by its filename.
 * Returns the REPORT_TYPES constant or null if not recognized.
 */
export const classifyReportByFilename = (filename) => {
  if (!filename) return null;
  const lower = filename.toLowerCase();

  // PFT: pulmonary function test
  if (
    lower.includes("pft") ||
    lower.includes("pulmonary_function") ||
    lower.includes("pulmonary function") ||
    lower.includes("spirometry") ||
    lower.includes("spirometr")
  ) {
    return REPORT_TYPES.PFT;
  }

  // Imaging: radiology, x-ray, CT, MRI, ultrasound, etc.
  if (
    lower.includes("xray") ||
    lower.includes("x-ray") ||
    lower.includes("x_ray") ||
    lower.includes("cxr") ||
    lower.includes("radiograph") ||
    lower.includes("radiology") ||
    lower.includes("imaging") ||
    lower.includes("ct") ||
    lower.includes("cat scan") ||
    lower.includes("computed tomography") ||
    lower.includes("mri") ||
    lower.includes("magnetic resonance") ||
    lower.includes("ultrasound") ||
    lower.includes("sonogram") ||
    lower.includes("echo") ||
    lower.includes("echocardiogram") ||
    lower.includes("fluoroscopy") ||
    lower.includes("nuclear") ||
    lower.includes("pet scan") ||
    lower.includes("mammogram") ||
    lower.includes("bone scan") ||
    lower.includes("chest_x") ||
    lower.includes("chest x") ||
    lower.includes("chest-x") ||
    lower.includes("chest_ray") ||
    lower.includes("chestray")
  ) {
    return REPORT_TYPES.CXR;
  }

  // Sleep Study: polysomnography (checked before CBC to avoid "sleep lab" matching lab keywords)
  if (
    lower.includes("sleep_study") ||
    lower.includes("sleep study") ||
    lower.includes("sleep_lab") ||
    lower.includes("sleep lab") ||
    lower.includes("sleepstudy") ||
    lower.includes("psg") ||
    lower.includes("polysomnograph") ||
    lower.includes("cpap_titration") ||
    lower.includes("cpap titration")
  ) {
    return REPORT_TYPES.SLEEP_STUDY;
  }

  // CBC / Lab results
  if (
    lower.includes("cbc") ||
    lower.includes("complete_blood") ||
    lower.includes("complete blood") ||
    lower.includes("blood_count") ||
    lower.includes("blood count") ||
    lower.includes("lab_result") ||
    lower.includes("lab result") ||
    lower.includes("labs_") ||
    lower.startsWith("lab_") ||
    lower.endsWith("_lab") ||
    lower.endsWith("_labs")
  ) {
    return REPORT_TYPES.CBC;
  }

  return null;
};

/**
 * Classify a report by its extracted metadata (report_type and test_name from file_classification_cache).
 * This is more reliable than filename matching when metadata has been extracted.
 * `metadata` is the metadata object; `classification` is the category string from the backend.
 */
export const classifyReportByMetadata = (metadata, classification) => {
  if (!metadata && !classification) return null;

  // Coerce to string so .toLowerCase() never throws (metadata can be number, array, object from DB/API)
  const reportType = String(metadata?.report_type ?? "").toLowerCase();
  const testName = String(metadata?.test_name ?? "").toLowerCase();
  const category = String(classification ?? "").toLowerCase();
  const combined = `${reportType} ${testName} ${category}`;

  // PFT: pulmonary function test
  if (
    /pulmonary.func|pft\b|spirometr|fev1|fvc\b|dlco|diffusing.capac|lung.function/.test(combined)
  ) {
    return REPORT_TYPES.PFT;
  }

  // Imaging: radiology, x-ray, CT, MRI, ultrasound, etc.
  if (
    /x-ray|xray|x_ray|cxr\b|radiograph|radiology|imaging\b|ct\s|ct scan|computed.tomography|mri\b|magnetic.resonance|ultrasound|sonogram|echo\b|echocardiogram|fluoroscopy|nuclear|pet.scan|mammogram|bone.scan|chest.x/.test(
      combined,
    )
  ) {
    return REPORT_TYPES.CXR;
  }

  // Sleep Study (checked before CBC to avoid "sleep lab" matching lab keywords)
  if (/sleep.study|sleep.lab|polysomnograph|psg\b|sleep.apnea|cpap.titration|actigraphy/.test(combined)) {
    return REPORT_TYPES.SLEEP_STUDY;
  }

  // CBC / Lab results
  if (
    /cbc\b|complete.blood|blood.count|differential|hematolog|lab\b|labs\b|laboratory/.test(
      combined,
    )
  ) {
    return REPORT_TYPES.CBC;
  }

  return null;
};

/**
 * Classify a report using metadata first (more accurate), then fall back to filename.
 * metadata: object from fileMetadata[filePath]
 * classification: string from fileClassifications[filePath]
 */
export const classifyReport = (filename, metadata, classification) => {
  const metaType = classifyReportByMetadata(metadata, classification);
  if (metaType) return metaType;
  return classifyReportByFilename(filename);
};

/**
 * Get the storage prefix for a report type (used for template file paths)
 */
export const getReportTypePrefix = (reportType) => {
  const map = {
    [REPORT_TYPES.PFT]: "pft",
    [REPORT_TYPES.CXR]: "cxr",
    [REPORT_TYPES.CBC]: "cbc",
    [REPORT_TYPES.SLEEP_STUDY]: "sleep",
  };
  return map[reportType] || reportType.toLowerCase();
};

/**
 * Default prompt for each report type.
 * fileName is optional - if provided it's included so the AI knows which file to focus on.
 */
export const getReportDefaultPrompt = (reportType, fileName) => {
  const fileRef = fileName ? ` from the file "${fileName}"` : "";

  switch (reportType) {
    case REPORT_TYPES.PFT:
      return `Please extract data${fileRef} from the PFT report. Extract the pre-bronchodilator and post-bronchodilator actual values and predicted percentages using the template format below.`;

    case REPORT_TYPES.CXR:
      return `Please extract the key findings and impression${fileRef} from this imaging/radiology report (e.g. x-ray, CT, MRI, ultrasound). Provide the impression or summary only.`;

    case REPORT_TYPES.CBC:
      return `Please give me the CBC / lab results${fileRef}. Follow the template format below exactly and fill in the values from the report. Important formatting rules:
- Use short date format (M/D/YY, e.g. 1/13/26)
- For ANCA, ESR: write "Normal" when within normal range
- For IgE: write "Normal [value]" (e.g. Normal 217)
- For ANA: write "Negative" or "Positive" as reported
- For the allergy panel, list only the numeric values — do not add "(Abnormal)" or any other labels
- Use bold headers and bullet points exactly as shown in the template`;

    case REPORT_TYPES.SLEEP_STUDY:
      return `Please give me the interpretation, leg movement findings, diagnosis, and recommendations from the sleep study report${fileRef}. Please provide a summarized, simple version of the result.`;

    default:
      return `Please extract the key findings and results${fileRef} from this report.`;
  }
};

/**
 * Default template for each report type.
 */
export const getReportDefaultTemplate = (reportType) => {
  switch (reportType) {
    case REPORT_TYPES.PFT:
      return `FEV1 actual and %: and cc: Pre ___ and post bronchodilator ___
FEV1/FVC (Actual): ____
FVC actual and %: Pre __ and post-bronchodilator ____
SVC actual and %: _____
TLC %: ____
RV %: _____
RV/TLC%: ____
DLCO%: ____
DL/VA%: ____`;

    case REPORT_TYPES.CXR:
      return `Impression / Key findings: ___`;

    case REPORT_TYPES.CBC:
      return `**Pertinent labs/Microbiology:** [Patient Last Name, First Name]-[Visit Date]

**[M/D/YY]**

**CBC with diff:**

- Low MCH: ___
- Low Plts: ___
- Eos: ___%
- Abs eos: ___

**CMP:**

- High Glucose: ___
- High ALT: ___

**ANCA:** ___

**IgE:** ___

**Allergy panel:**

- D pteronyssinus: ___
- D farinae: ___
- Penicillium chrysogen: ___
- Cladosporium herbarum: ___
- Aspergillus fumigatus: ___
- Alternaria Alternata: ___
- Mountain cedar: ___

**ESR:** ___

**ANA:** ___`;

    case REPORT_TYPES.SLEEP_STUDY:
      return `Interpretation: ___
Leg movements: ___
Diagnosis: ___
Recommendations: ___`;

    default:
      return "";
  }
};

/**
 * Load user's personal default template and prompt for a report type
 */
export const loadUserDefaultReportTemplate = async (reportType) => {
  try {
    const userName = await getCurrentUserName();
    if (!userName) return { templateText: null, promptText: null };

    const supabase = await getSupabase();
    const BUCKET_NAME = getBucketName();
    const typePrefix = getReportTypePrefix(reportType);
    const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
    const userFolder = `New/templates/reports_templates/${sanitizedUserName}`;

    const templatePath = `${userFolder}/default_template_${typePrefix}`;
    const promptPath = `${userFolder}/default_prompt_${typePrefix}`;

    const [templateResult, promptResult] = await Promise.all([
      supabase.storage.from(BUCKET_NAME).download(templatePath),
      supabase.storage.from(BUCKET_NAME).download(promptPath),
    ]);

    let templateText = null;
    let promptText = null;

    if (!templateResult.error && templateResult.data) {
      templateText = await templateResult.data.text();
    }
    if (!promptResult.error && promptResult.data) {
      promptText = await promptResult.data.text();
    }

    if (templateText && promptText) {
      console.log(`✅ reportProcessingUtils: Loaded user default template for ${reportType}`);
    }

    return { templateText, promptText };
  } catch (error) {
    console.warn("⚠️ reportProcessingUtils: Error loading user default report template:", error);
    return { templateText: null, promptText: null };
  }
};

/**
 * Load template and prompt from database (user default first, then global default, then hardcoded)
 */
export const loadReportTemplateAndPrompt = async (reportType, fileName) => {
  // First check for user's personal default
  const userDefault = await loadUserDefaultReportTemplate(reportType);
  if (userDefault.templateText && userDefault.promptText) {
    return userDefault;
  }

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const typePrefix = getReportTypePrefix(reportType);

  let templateText = null;
  let promptText = null;

  try {
    const { data: templateData, error: templateError } = await supabase.storage
      .from(BUCKET_NAME)
      .download(`New/templates/reports_templates/default_template_${typePrefix}`);

    if (!templateError && templateData) {
      templateText = await templateData.text();
      console.log(`✅ reportProcessingUtils: Loaded ${reportType} template from database`);
    }
  } catch (error) {
    console.warn("⚠️ reportProcessingUtils: Error loading report template from DB:", error);
  }

  try {
    const { data: promptData, error: promptError } = await supabase.storage
      .from(BUCKET_NAME)
      .download(`New/templates/reports_templates/default_prompt_${typePrefix}`);

    if (!promptError && promptData) {
      promptText = await promptData.text();
      console.log(`✅ reportProcessingUtils: Loaded ${reportType} prompt from database`);
    }
  } catch (error) {
    console.warn("⚠️ reportProcessingUtils: Error loading report prompt from DB:", error);
  }

  // Fall back to hardcoded defaults
  if (!templateText) {
    templateText = getReportDefaultTemplate(reportType);
  }
  if (!promptText) {
    promptText = getReportDefaultPrompt(reportType, fileName);
  }

  return { templateText, promptText };
};

/**
 * Load saved user templates for a specific report type
 */
export const loadSavedUserReportTemplates = async (reportType) => {
  try {
    const userName = await getCurrentUserName();
    if (!userName) {
      console.warn("⚠️ reportProcessingUtils: No user name, cannot load saved report templates");
      return [];
    }

    const supabase = await getSupabase();
    const BUCKET_NAME = getBucketName();
    const typePrefix = getReportTypePrefix(reportType);
    const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
    const userFolder = `New/templates/reports_templates/${sanitizedUserName}`;

    const { data: files, error } = await supabase.storage.from(BUCKET_NAME).list(userFolder);

    if (error) {
      console.warn("⚠️ reportProcessingUtils: Error listing user report templates:", error);
      return [];
    }

    const templateFiles = (files || [])
      .filter(
        (file) =>
          file.name.startsWith(`template_${typePrefix}`) && !file.name.includes("_prompt"),
      )
      .map((file) => {
        const match = file.name.match(/template_(\w+?)(\d+)$/);
        return match ? parseInt(match[2]) : null;
      })
      .filter((num) => num !== null)
      .sort((a, b) => a - b);

    const savedTemplates = [];
    for (const num of templateFiles) {
      try {
        const templatePath = `${userFolder}/template_${typePrefix}${num}`;
        const promptPath = `${userFolder}/template_${typePrefix}_prompt${num}`;

        const [templateResult, promptResult] = await Promise.all([
          supabase.storage.from(BUCKET_NAME).download(templatePath),
          supabase.storage.from(BUCKET_NAME).download(promptPath),
        ]);

        if (!templateResult.error && !promptResult.error) {
          const templateText = await templateResult.data.text();
          const promptText = await promptResult.data.text();
          savedTemplates.push({
            index: num,
            template: templateText,
            prompt: promptText,
            name: `Template ${typePrefix.toUpperCase()} ${num}`,
          });
        }
      } catch (error) {
        console.warn(`⚠️ reportProcessingUtils: Error loading report template ${num}:`, error);
      }
    }

    console.log(
      `✅ reportProcessingUtils: Loaded ${savedTemplates.length} saved templates for ${reportType}`,
    );
    return savedTemplates;
  } catch (error) {
    console.error("❌ reportProcessingUtils: Error loading saved report templates:", error);
    return [];
  }
};

/**
 * Save a new numbered user report template
 */
export const saveUserReportTemplate = async (reportType, templateText, promptText) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const typePrefix = getReportTypePrefix(reportType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/reports_templates/${sanitizedUserName}`;

  const savedTemplates = await loadSavedUserReportTemplates(reportType);
  const nextNumber =
    savedTemplates.length > 0 ? Math.max(...savedTemplates.map((t) => t.index)) + 1 : 1;

  const templatePath = `${userFolder}/template_${typePrefix}${nextNumber}`;
  const promptPath = `${userFolder}/template_${typePrefix}_prompt${nextNumber}`;

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage.from(BUCKET_NAME).upload(templatePath, new Blob([templateText], { type: "text/plain" }), {
      upsert: true,
      contentType: "text/plain",
    }),
    supabase.storage.from(BUCKET_NAME).upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
      upsert: true,
      contentType: "text/plain",
    }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message || promptResult.error?.message || "Upload failed",
    );
  }

  console.log(`✅ reportProcessingUtils: Saved report template ${typePrefix}${nextNumber}`);
  return nextNumber;
};

/**
 * Update an existing numbered user report template
 */
export const updateUserReportTemplate = async (
  reportType,
  templateText,
  promptText,
  templateIndex,
) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const typePrefix = getReportTypePrefix(reportType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/reports_templates/${sanitizedUserName}`;

  const savedTemplates = await loadSavedUserReportTemplates(reportType);
  if (templateIndex < 0 || templateIndex >= savedTemplates.length) {
    throw new Error("Invalid template index");
  }

  const templateNumber = savedTemplates[templateIndex].index;
  const templatePath = `${userFolder}/template_${typePrefix}${templateNumber}`;
  const promptPath = `${userFolder}/template_${typePrefix}_prompt${templateNumber}`;

  try {
    await Promise.allSettled([
      supabase.storage.from(BUCKET_NAME).remove([templatePath]),
      supabase.storage.from(BUCKET_NAME).remove([promptPath]),
    ]);
  } catch (removeError) {
    console.log("Note: Error during file removal, will proceed with upload");
  }

  await new Promise((resolve) => setTimeout(resolve, 150));

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage.from(BUCKET_NAME).upload(templatePath, new Blob([templateText], { type: "text/plain" }), {
      upsert: true,
      contentType: "text/plain",
    }),
    supabase.storage.from(BUCKET_NAME).upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
      upsert: true,
      contentType: "text/plain",
    }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message || promptResult.error?.message || "Upload failed",
    );
  }

  console.log(`✅ reportProcessingUtils: Updated report template ${typePrefix}${templateNumber}`);
  await new Promise((resolve) => setTimeout(resolve, 300));
  return templateNumber;
};

/**
 * Save user's personal default report template and prompt
 */
export const saveUserDefaultReportTemplate = async (reportType, templateText, promptText) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const typePrefix = getReportTypePrefix(reportType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/reports_templates/${sanitizedUserName}`;

  const templatePath = `${userFolder}/default_template_${typePrefix}`;
  const promptPath = `${userFolder}/default_prompt_${typePrefix}`;

  try {
    await Promise.allSettled([
      supabase.storage.from(BUCKET_NAME).remove([templatePath]),
      supabase.storage.from(BUCKET_NAME).remove([promptPath]),
    ]);
  } catch (removeError) {
    console.log("Note: Error during file removal, will proceed with upload");
  }

  await new Promise((resolve) => setTimeout(resolve, 150));

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage.from(BUCKET_NAME).upload(templatePath, new Blob([templateText], { type: "text/plain" }), {
      upsert: true,
      contentType: "text/plain",
    }),
    supabase.storage.from(BUCKET_NAME).upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
      upsert: true,
      contentType: "text/plain",
    }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message || promptResult.error?.message || "Upload failed",
    );
  }

  console.log(`✅ reportProcessingUtils: Saved user default report template for ${reportType}`);
};

// Promise-based lock: concurrent calls share the same in-flight promise.
// Re-set to null on page reload (module re-evaluation) so it always runs once per load.
let _initReportTemplatesPromise = null;

/**
 * Initialize global default templates/prompts for all report types in Supabase storage.
 * - Lists the folder once to see what already exists, uploads only missing files.
 * - upsert:false as a safety net — Supabase rejects any accidental overwrites.
 * - Cleans up any duplicate entries (same name, different id) left from previous runs.
 * - Promise lock prevents concurrent duplicate calls (e.g. React StrictMode double-mount).
 */
export const initializeDefaultReportTemplates = () => {
  if (_initReportTemplatesPromise) return _initReportTemplatesPromise;
  _initReportTemplatesPromise = _runInitReportTemplates().catch((err) => {
    _initReportTemplatesPromise = null; // reset on error so next load can retry
    console.warn("⚠️ reportProcessingUtils: Error initializing default report templates:", err);
  });
  return _initReportTemplatesPromise;
};

const _runInitReportTemplates = async () => {
  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const basePath = "New/templates/reports_templates";

  // Single list() call — tells us exactly what files exist right now
  const { data: existingFiles } = await supabase.storage.from(BUCKET_NAME).list(basePath);
  const existing = new Set((existingFiles || []).map((f) => f.name));

  // Build the full set of expected file names
  const expectedNames = new Set();
  for (const reportType of Object.values(REPORT_TYPES)) {
    const typePrefix = getReportTypePrefix(reportType);
    expectedNames.add(`default_template_${typePrefix}`);
    expectedNames.add(`default_prompt_${typePrefix}`);
  }

  // Remove duplicates: keep only the first entry for each expected name,
  // delete any extras (same name appearing more than once in the listing)
  const seen = new Set();
  const toDelete = [];
  for (const file of existingFiles || []) {
    if (expectedNames.has(file.name)) {
      if (seen.has(file.name)) {
        toDelete.push(`${basePath}/${file.name}`);
      } else {
        seen.add(file.name);
      }
    }
  }
  if (toDelete.length > 0) {
    await supabase.storage.from(BUCKET_NAME).remove(toDelete);
    console.log(`🗑️ reportProcessingUtils: Removed ${toDelete.length} duplicate(s)`);
  }

  // Upload only files that are missing
  for (const reportType of Object.values(REPORT_TYPES)) {
    const typePrefix = getReportTypePrefix(reportType);
    const filePairs = [
      [`default_template_${typePrefix}`, getReportDefaultTemplate(reportType)],
      [`default_prompt_${typePrefix}`, getReportDefaultPrompt(reportType)],
    ];

    for (const [fileName, content] of filePairs) {
      if (existing.has(fileName)) continue; // already exists, skip

      const { error } = await supabase.storage.from(BUCKET_NAME).upload(
        `${basePath}/${fileName}`,
        new Blob([content], { type: "text/plain" }),
        { upsert: false, contentType: "text/plain" },
      );

      if (error) {
        console.warn(`⚠️ reportProcessingUtils: Failed to create ${fileName}:`, error.message);
      } else {
        console.log(`✅ reportProcessingUtils: Created ${fileName}`);
      }
    }
  }
};

/**
 * Prepare all data needed to populate the ProcessReportPromptEditor modal.
 * reportType: one of REPORT_TYPES constants
 * fileName: the name of the specific file being processed (optional, included in prompt)
 */
export const prepareReportProcessingData = async (reportType, fileName) => {
  const { templateText, promptText } = await loadReportTemplateAndPrompt(reportType, fileName);
  const savedTemplates = await loadSavedUserReportTemplates(reportType);

  return {
    reportType,
    reportTypeLabel: REPORT_TYPE_LABELS[reportType] || reportType,
    templateInfo: templateText || getReportDefaultTemplate(reportType),
    editablePrompt: promptText || getReportDefaultPrompt(reportType, fileName),
    savedTemplates,
    fileName,
  };
};
